import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { EnergyClassification } from '@components/atoms/EnergyClassification';
import { ContactForm, ContactFormData } from '@components/molecules/ContactForm';
import { ContactBox } from '@components/molecules/ContactBox';
import { PriceBox } from '@components/molecules/PriceBox';
import { SpacePhotoGrid } from '@components/molecules/SpacePhotoGrid';
import { useNotifications } from '@hooks/useNotifications';
import { useToggleFavoriteSpace } from '@services/useToggleFavoriteSpace';

import areaIcon from '@assets/icons/area-icon.svg';
import floorIcon from '@assets/icons/floor-icon.svg';
import conditionIcon from '@assets/icons/condition-icon.svg';
import houseIcon from '@assets/icons/house-icon.svg';
import { SpaceItem, GalleryItem } from '@services/useGetSpaces';
import { useSpaceContactRequest } from '@services/useSpaceContactRequest';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { icon } from 'leaflet';
import mapPinIcon from '@assets/images/map-pin.svg';

interface SpaceDetailContainerProps {
    space: SpaceItem;
    loading: boolean;
}

const pinIcon = icon({
    iconUrl: mapPinIcon,
    iconSize: [38, 95],
    shadowSize: [50, 64],
    iconAnchor: [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76],
});

const Separator: FC = () => <hr style={{ borderTop: '1px solid #ccc', margin: '10px 0' }} />;

const extractGalleryImages = (galleries: GalleryItem[]): string[] => {
    if (!galleries || !Array.isArray(galleries)) {
        return [];
    }
    return galleries.map(gallery => gallery.url).filter(Boolean);
};

// Componentes internos reutilizáveis
const SpacePhotos: FC<{ images: string[] }> = ({ images }) => (
    <SpacePhotoGrid images={images} />
);

const SpaceTitleSection: FC<{ space: SpaceItem }> = ({ space }) => (
    <>
        <Typography fontSize="14" color="primary">
            {space.featuredType.label}
        </Typography>
        <Typography classes="mt-2 mb-5" fontWeight="900" tagVariant={TypographyTagVariants.h2}>
            {space.title}
        </Typography>
    </>
);

const SpaceDescription: FC<{ space: SpaceItem; translations: any }> = ({ space, translations }) => (
    <>
        <Typography fontSize="24" fontWeight="900">
            {translations.descriptionTitle}
        </Typography>
        <Typography color="muted">{space.description}</Typography>
    </>
);

const SpaceLocation: FC<{ space: SpaceItem; translations: any }> = ({ space, translations }) => {
    const lat = parseFloat(space.latitude);
    const lng = parseFloat(space.longitude);
    const hasValidCoordinates = !isNaN(lat) && !isNaN(lng);

    return (
        <>
            <Typography classes="mt-5" fontSize="24" fontWeight="900">
                {translations.locationTitle}
            </Typography>
            {hasValidCoordinates ? (
                <div style={{ height: 410, marginTop: '1rem' }}>
                    <MapContainer 
                        center={[lat, lng]} 
                        zoom={15} 
                        scrollWheelZoom={false}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker icon={pinIcon} position={[lat, lng]}>
                            <span>{space.title}</span>
                        </Marker>
                    </MapContainer>
                </div>
            ) : (
                <div style={{ height: 410, background: '#d9d9d9', marginTop: '1rem' }}>
                    <p>{translations.map}</p>
                </div>
            )}
        </>
    );
};

interface SpaceFeaturesProps {
    space: SpaceItem;
    translations: any;
}

const SpaceFeatures: FC<SpaceFeaturesProps> = ({ space, translations }) => (
    <>
        <Typography classes="mt-5" fontSize="24" fontWeight="900">
            {translations.featuresTitle}
        </Typography>
        <div className="row mt-3">
            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between">
                    <Typography color="muted">
                        <img src={areaIcon} alt="tamanho" style={{ marginRight: '10px' }} />
                        {translations.areaLabel}
                    </Typography>
                    <div>{space.areaM2} m²</div>
                </div>
                <Separator />
            </div>

            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between">
                    <Typography color="muted">
                        <i className="fa-regular fa-calendar" style={{ marginRight: '10px' }} />
                        {translations.constructionYearLabel}
                    </Typography>
                    <div>{space.constructionYear}</div>
                </div>
                <Separator />
            </div>

            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between">
                    <Typography color="muted">
                        <img src={floorIcon} alt="Pavimentos" style={{ marginRight: '10px' }} />
                        {translations.floorsLabel}
                    </Typography>
                    <div>{space.numberOfFloors}</div>
                </div>
                <Separator />
            </div>

            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between">
                    <Typography color="muted">
                        <img src={houseIcon} alt="Certificado energetico" style={{ marginRight: '10px' }} />
                        {translations.energyCertificateLabel}
                    </Typography>
                    <div>
                        <EnergyClassification
                            letter={space.energyCertificate.code} />
                    </div>
                </div>
                <Separator />
            </div>

            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between">
                    <Typography color="muted">
                        <img src={conditionIcon} alt="Ícone condição" style={{ marginRight: '10px' }} />
                        {translations.conditionLabel}
                    </Typography>
                    <div>{space.spaceStatus.name}</div>
                </div>
                <Separator />
            </div>
        </div>
    </>
);

interface SpaceDocumentationProps {
    space: SpaceItem;
    translations: any;
}

const SpaceDocumentation: FC<SpaceDocumentationProps> = ({ space, translations }) => (
    <>
        {space.documents && space.documents.length > 0 && (
            <div className="mt-4">
                <Typography classes="mb-3" fontSize="24" fontWeight="900">
                    {translations.documentsTitle}
                </Typography>
                <div className="row">
                    {space.documents.map((doc) => (
                        <div key={doc.uuid} className="col-12 col-md-6 col-lg-4">
                            <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: 'none',
                                    color: '#333',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <i
                                    className="fa-regular fa-file-pdf"
                                    style={{ marginRight: '8px' }}
                                />
                                <Typography>{doc.name}</Typography>
                            </a>
                            <hr style={{ borderTop: '1px solid #ccc', margin: '10px 0' }} />
                        </div>
                    ))}
                </div>
            </div>
        )}
    </>
);

export const SpaceDetailContainer: FC<SpaceDetailContainerProps> = ({ space, loading }) => {
    const { t } = useTranslation();
    const { request: requestContactSpace, loading: submitting, success } = useSpaceContactRequest();
    const { showDefaultNotification } = useNotifications();

    const {
        request: requestToggleFavorite,
        loading: loadingToggleFavorite,
        error: errorToggleFavorite,
        lastResponseTime: lastToggleFavorite,
    } = useToggleFavoriteSpace();
    
    const [favorite, setFavorite] = useState<boolean>(false);

    useEffect(() => {
        if (space) {
            setFavorite(space.like || false);
        }
    }, [space]);

    const translations = {
        loading: t('space-detail-component.loading'),
        photos: t('space-detail-component.photos'),
        descriptionTitle: t('space-detail-component.description-title'),
        locationTitle: t('space-detail-component.location-title'),
        map: t('space-detail-component.map'),
        featuresTitle: t('space-detail-component.features-title'),
        areaLabel: t('space-detail-component.area-label'),
        constructionYearLabel: t('space-detail-component.construction-year-label'),
        floorsLabel: t('space-detail-component.floors-label'),
        energyCertificateLabel: t('space-detail-component.energy-certificate-label'),
        conditionLabel: t('space-detail-component.condition-label'),
        documentationTitle: t('space-detail-component.documentation-title'),
        document: t('space-detail-component.document'),
        download: t('space-detail-component.download'),
        documentsTitle: t('space-detail-component.documents-title'),
        phoneLabel: t('contact-box-component.phone-label'),
        emailLabel: t('contact-box-component.email-label'),
        facebookLabel: t('contact-box-component.facebook-label'),
        instagramLabel: t('contact-box-component.instagram-label'),
        formLabel: t('contact-box-component.form-label'),
        showNumber: t('space-detail-component.show-number'),
        contactSuccessMessage: t('space-detail-component.contact-success-message'),
    };

    useEffect(() => {
        if (success) {
            showDefaultNotification({
                type: 'success',
                message: translations.contactSuccessMessage,
            });
        }
        // eslint-disable-next-line
    }, [success]);

    const handleOnClickFavorite = () => {
        if (!loadingToggleFavorite) {
            requestToggleFavorite({ uuid: space.uuid || '' });
        }
    };

    useEffect(() => {
        if (lastToggleFavorite && !errorToggleFavorite) {
            setFavorite(!favorite);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastToggleFavorite]);

    const getContactIcon = (type: string): string => {
        switch (type.toLowerCase()) {
            case 'telefone':
                return 'fa-solid fa-phone';
            case 'email':
                return 'fa-regular fa-envelope';
            case 'facebook':
                return 'fa-brands fa-facebook';
            case 'instagram':
                return 'fa-brands fa-instagram';
            case 'form':
                return 'fa-regular fa-file-lines';
            default:
                return 'fa-solid fa-info-circle';
        }
    };

    const getContactLabel = (type: string): string => {
        switch (type.toLowerCase()) {
            case 'telefone':
                return translations.phoneLabel;
            case 'email':
                return translations.emailLabel;
            case 'facebook':
                return translations.facebookLabel;
            case 'instagram':
                return translations.instagramLabel;
            case 'form':
                return translations.formLabel;
            default:
                return type;
        }
    };

    const handleFormSubmit = async (data: ContactFormData) => {
        await requestContactSpace(space.uuid, data);
    };

    if (loading || !space) {
        return (
            <div data-testid="space-detail-container">
                <Container>
                    <div className="row">
                        <div className="col-12">
                            <p>{translations.loading}</p>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    const contactsData = space.contacts?.map(contact => ({
        icon: getContactIcon(contact.type),
        label: getContactLabel(contact.type),
        value: contact.value,
    })) || [];

    // Parse price from string format "5.069,00 €"
    const priceValue = parseFloat(space.price.replace(/[^\d,]/g, '').replace(',', '.'));

    return (
        <div data-testid="space-detail-container">
            <Container>
                <div className="row">
                    <div className="col-12 mb-4">
                        <SpacePhotos images={extractGalleryImages(space.galleries)} />
                    </div>

                    <div className="col-lg-8 col-12">
                        <SpaceTitleSection space={space} />
                    </div>

                    <div className="col-lg-4 col-12 mb-4">
                        <PriceBox 
                            price={priceValue} 
                            onLikeClick={handleOnClickFavorite}
                            isLiked={favorite}
                        />
                    </div>

                    <div className="col-lg-8">
                        <SpaceDescription space={space} translations={translations} />
                        <SpaceLocation space={space} translations={translations} />
                        <SpaceFeatures space={space} translations={translations} />
                        <SpaceDocumentation space={space} translations={translations} />
                    </div>

                    <div className="col-lg-4">
                        {contactsData.length > 0 && (
                            <ContactBox contacts={contactsData} />
                        )}

                        <ContactForm
                            onSubmit={handleFormSubmit}
                            loading={submitting}
                            onSuccess={success ? () => {} : undefined}
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
};